import { reactive } from 'vue';
import { taxHistoryApi } from '../services/taxHistoryApi.js';

/**
 * Reactive cache for the Tax History pages (S1-P6). The live API
 * (`services/taxHistoryApi.js`) is the source of truth; this store holds the
 * mapped, UI-shaped copy that the list + detail views render.
 *
 * Replaces the old 3-tier mock (users → submissions → client-recalculated
 * detail) with a thin 2-tier API cache (list of forms → form detail). The
 * mock `state.users`/`state.submissions` and the user-centric functions
 * (getHistoryUsers / getSubmissionsByUser / getSubmissionDetail / getUserById /
 * deleteSubmission) plus the `taxCalculator`/`getDefaultTaxInput` dependency are
 * gone.
 *
 * @typedef {import('../services/taxHistoryApi.js').TaxFormSummaryDto} TaxFormSummaryDto
 * @typedef {import('../services/taxHistoryApi.js').TaxFormFullDto} TaxFormFullDto
 * @typedef {import('../services/taxHistoryApi.js').TaxFormListResult} TaxFormListResult
 */

const state = reactive({
  /** @type {TaxFormSummaryDto[]} current page of forms */
  forms: [],
  totalCount: 0,
  page: 1,
  pageSize: 50,
  totalPages: 0,
  loading: false,
  error: '',
  /** @type {?TaxFormFullDto} currently open form detail */
  detail: null,
  detailLoading: false,
  detailError: '',
  /** @type {?number} HTTP/API status of the last detail error (so the view can detect 404) */
  detailErrorStatus: null,
});

/**
 * Load a page of tax forms into the cache.
 * @param {{ status?:string, yearId?:number, sessionId?:string, userId?:string, page?:number, pageSize?:number }} [filter]
 * @returns {Promise<TaxFormListResult>}
 */
async function loadForms({ status, yearId, sessionId, userId, page, pageSize } = {}) {
  state.loading = true;
  state.error = '';
  try {
    const result = await taxHistoryApi.list({ status, yearId, sessionId, userId, page, pageSize });
    state.forms = (result && result.items) || [];
    state.totalCount = (result && result.totalCount) || 0;
    state.page = (result && result.page) || page || 1;
    state.pageSize = (result && result.pageSize) || pageSize || 50;
    state.totalPages = (result && result.totalPages) || 0;
    return result;
  } catch (e) {
    state.error = e.message;
    state.forms = [];
    throw e;
  } finally {
    state.loading = false;
  }
}

/**
 * Load a single form's full detail (form + answers) into the cache.
 * On failure, stores the message AND the http/api status so the view can detect
 * a 404 (not-found card) vs other errors (retry alert).
 * @param {number|string} formId
 * @returns {Promise<TaxFormFullDto>}
 */
async function loadFormDetail(formId) {
  state.detailLoading = true;
  state.detailError = '';
  state.detailErrorStatus = null;
  state.detail = null;
  try {
    const result = await taxHistoryApi.getById(formId);
    state.detail = result;
    return result;
  } catch (e) {
    state.detail = null;
    state.detailError = e.message;
    state.detailErrorStatus = e.httpStatus ?? e.status ?? null;
    throw e;
  } finally {
    state.detailLoading = false;
  }
}

/** Zero-pad a number to 2 digits. */
function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Format an ISO timestamp → stable `DD/MM/YYYY HH:mm`.
 * Returns '—' for null/undefined/invalid input (e.g. InProgress forms have
 * `completedAt === null`, so the list "Completed" column and detail "End" field
 * must not break).
 * @param {?string} iso
 * @returns {string}
 */
function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export { state, loadForms, loadFormDetail, formatDate };
